import { ERROR_MESSAGES, VIEW } from './enums';
import { Fragment, FunctionalComponent, h } from 'preact';
import { RTL_LANGUAGES, UC_UI_GLOBAL_NAMESPACE } from './constants';
import { StateUpdater, useState } from 'preact/hooks';
import { Suspense, lazy } from 'preact/compat';
import { Theme, generateTheme } from './styles';
import Usercentrics, {
  BaseService,
  InitialUIValues,
  Service,
  Settings,
  TCFVendor,
  TCF_DECISION_UI_LAYER,
  TRACKING_EVENT_TYPE,
  UI_VARIANT,
} from '@usercentrics/cmp-browser-sdk';
import { useEmbeddings, useInitApp, useMutation } from './hooks';
import { TAB_VIEW } from './enums/Tabs';
import { getCurrentUiCategoriesChoices } from './App.helpers';
import { setTrackingPixel } from './modules/helpers';
import { useCategoryContext } from './contexts/CategoryContext';
import { useSettingsContext } from './contexts/SettingsContext';
import { useTcfContext } from './contexts/TcfContext';
import { useUiContext } from './contexts/UiContext';

let isUIInitialized = false;

type Props = {
  initialUIValues: InitialUIValues;
  onThemeChange: StateUpdater<Theme>;
  restartCMP: () => Promise<void>;
  UC: Usercentrics;
};

const View = lazy(() => import('./views'));

const App: FunctionalComponent<Props> = ({ initialUIValues, restartCMP, onThemeChange, UC }) => {
  const [, setToggleCcpaChecked] = useState(false);
  const { categories, setCategories } = useCategoryContext();
  const { setTcfData } = useTcfContext();
  const { settings, setSettings } = useSettingsContext();
  const {
    clearServiceVendorScrollToId,
    isAmpEnabled,
    setIsInFullScreen,
    setLanguage,
    setScrollToIdTabViewServiceVendor,
    setTabView,
    setView,
    settingsCloseView,
    uiVariant,
    updateView,
    view,
  } = useUiContext();

  const [dismissAmp, { loading: dismissAmpLoading }] = useMutation(UC.dismissAmp, UC);
  const [changeLanguage, { loading: changeLanguageLoading }] = useMutation(UC.changeLanguage, UC);
  const [saveOptOutForCcpa, { loading: saveOptOutForCcpaLoading }] = useMutation(UC.saveOptOutForCcpa, UC);
  const [updateServices, { loading: updateServicesLoading }] = useMutation(UC.updateServices, UC);
  const [acceptAllServices, { loading: acceptAllServicesLoading }] = useMutation(UC.acceptAllServices, UC);
  const [acceptAllForTCF, { loading: acceptAllForTCFLoading }] = useMutation(UC.acceptAllForTCF, UC);
  const [acceptAllAmp, { loading: acceptAllAmpLoading }] = useMutation(UC.acceptAllAmp, UC);
  const [denyAllServices, { loading: denyAllServicesLoading }] = useMutation(UC.denyAllServices, UC);
  const [denyAllForTCF, { loading: denyAllForTCFLoading }] = useMutation(UC.denyAllForTCF, UC);
  const [denyAllAmp, { loading: denyAllAmpLoading }] = useMutation(UC.denyAllAmp, UC);

  const onAppInitialized = () => {
    isUIInitialized = true;
  };

  useInitApp(UC, initialUIValues, onThemeChange, onAppInitialized);
  useEmbeddings(UC);

  /**
   * GLOBALLY EXPOSED METHODS
   * Offer global methods for plain HTML/JS websites. Those methods are added to the Browser's window object under the
   * UC_UI namespace. E.g. to open the secondLayer from the dev console you can type UC_UI.showSecondLayer().
   */
  /** A method that will open the firstLayer. */
  const showFirstLayer = async (): Promise<void> => {
    return setView(VIEW.FIRST_LAYER);
  };

  /** A method that will open the secondLayer. */
  /** If an ID of a vendor or service is given it will directly open the right tab, scrolls to the entry and expands the service/vendor */
  const showSecondLayer = async (scrollToServiceVendorId?: string | number): Promise<void> => {
    if (scrollToServiceVendorId) {
      setTabView(TAB_VIEW.SERVICES_VENDORS);
      setScrollToIdTabViewServiceVendor(scrollToServiceVendorId);
    } else {
      clearServiceVendorScrollToId();
    }

    return setView(VIEW.SECOND_LAYER);
  };

  /**
   * A method for accepting or denying a single service. Is currently used by the embeddings feature and Usercentric's
   * Smart Blocker/Privacy Shield
   * @param serviceId the id of the service
   * @param isAccept decision type
   */
  const acceptDenyService = async (serviceId: string, isAccept: boolean): Promise<void> => {
    if (!serviceId || typeof serviceId !== 'string') {
      throw new Error(ERROR_MESSAGES.SERVICE_MISSING_ID);
    }

    if (updateServicesLoading) {
      return;
    }

    updateServices([{ serviceId, status: isAccept }]).then(() => {
      setTrackingPixel(TRACKING_EVENT_TYPE.CUSTOM_CHOICE, UC.setTrackingPixel);
      setCategories(UC.getCategories());
    });
  };

  /**
   * A method to accept or deny and close CCPA CMP
   * @param isAccept decision type
   */
  const acceptDenyCcpa = async (isAccept: boolean): Promise<void> => {
    if (saveOptOutForCcpaLoading) {
      return;
    }
    const isOptedOut = !isAccept;

    setToggleCcpaChecked(isOptedOut);
    saveOptOutForCcpa(isOptedOut);
  };

  /**
   * A method to accept all default services
   */
  const acceptAllDefault = async (): Promise<void> => {
    if (acceptAllServicesLoading) {
      return;
    }

    acceptAllServices().then(async () => {
      if (isAmpEnabled) {
        await acceptAllAmp();
      }
      setTrackingPixel(TRACKING_EVENT_TYPE.ACCEPT_ALL, UC.setTrackingPixel);
      setCategories(UC.getCategories());
    });
  };

  /**
   * A method to accept all tcf consents
   */
  const acceptAllTCF = async (): Promise<void> => {
    if (acceptAllServicesLoading || acceptAllForTCFLoading || acceptAllAmpLoading) {
      return;
    }

    const fromLayer =
      view === VIEW.FIRST_LAYER ? TCF_DECISION_UI_LAYER.FIRST_LAYER : TCF_DECISION_UI_LAYER.SECOND_LAYER;

    // For TCF we need to wait for both the services and the vendors/purposes to be updated before continuing.
    Promise.all([acceptAllServices(), acceptAllForTCF(fromLayer)]).then(async () => {
      if (isAmpEnabled) {
        await acceptAllAmp();
      }
      setTrackingPixel(TRACKING_EVENT_TYPE.ACCEPT_ALL, UC.setTrackingPixel);
      setCategories(UC.getCategories());
      setTcfData(UC.getTCFData());
    });
  };

  /**
   * A method to accept all consents
   * If UI_VARIANT not defined, method returns null
   */
  const acceptAllConsents = (): Promise<void> | null => {
    switch (initialUIValues.variant) {
      case UI_VARIANT.CCPA:
        return acceptDenyCcpa(true);
      case UI_VARIANT.DEFAULT:
        return acceptAllDefault();
      case UI_VARIANT.TCF:
        return acceptAllTCF();
      default:
        return null;
    }
  };

  /**
   * This method should always be triggered when clicking on close button on the second layer of Default UI and CCPA.
   */
  const closeCMP = async (): Promise<void> => {
    if (dismissAmpLoading) {
      return;
    }

    if (isAmpEnabled) {
      await dismissAmp();
      setIsInFullScreen(false);

      if (settings.ui.privacyButton.isEnabled) {
        updateView(VIEW.PRIVACY_BUTTON);
      } else {
        updateView(VIEW.NONE);
      }
    } else {
      // The settingsCloseView always provides a reference to the view that the secondLayer was opened from. If the
      // secondLayer was opened from the firstLayer, then we will go back to the firstLayer (the user has to make a
      // choice!). If the secondLayer was opened from the Privacy Button / using a button from the host website, we will
      // go back to the Privacy Button View / no view respectively.
      updateView(settingsCloseView);
    }
  };

  /**
   * A method to clear localStorage
   */
  const clearStorage = (): Promise<void> => UC.clearStorage();

  /**
   * A method to get current active language
   */
  const getActiveLanguage = (): string => UC.getSettings().ui.language.selected;

  /**
   * A method to get all services
   * @deprecated since version 2.2.9
   * Will be deleted in version 3.0.
   *
   * In case you need similar behavior, use getServicesBaseInfo or getServicesFullInfo
   *
   
   */
  const getServices = (): Service[] => UC.getServices();

  /**
   * A method to get all services with base information
   */
  const getServicesBaseInfo = (): BaseService[] => UC.getServicesBaseInfo();

  /**
   * A method to get all services with full information (aggregator call)
   */
  const getServicesFullInfo = (): Promise<Service[]> => UC.getServicesFullInfo();

  /**
   * A method to get all settings
   */
  const getSettings = (): Settings => UC.getSettings();

  /**
   * A method to check If UI is initialized
   */
  const isInitialized = (): boolean => isUIInitialized;

  /**
   * A method that exposes the TCF Vendors
   * If TCF is disabled, method returns undefined
   */
  const getTCFVendors = (): TCFVendor[] | undefined => {
    return UC.getTCFData()?.vendors;
  };

  /**
   * A method to deny all default services
   */
  const denyAllDefault = async (): Promise<void> => {
    if (denyAllServicesLoading || denyAllAmpLoading) {
      return;
    }

    denyAllServices().then(async () => {
      if (isAmpEnabled) {
        await denyAllAmp();
      }
      setTrackingPixel(TRACKING_EVENT_TYPE.DENY_ALL, UC.setTrackingPixel);
      setCategories(UC.getCategories());
    });
  };

  /**
   * A method to deny all tcf consents
   */
  const denyAllTCF = async (): Promise<void> => {
    if (denyAllServicesLoading || denyAllForTCFLoading) {
      return;
    }

    const fromLayer =
      view === VIEW.FIRST_LAYER ? TCF_DECISION_UI_LAYER.FIRST_LAYER : TCF_DECISION_UI_LAYER.SECOND_LAYER;

    // For TCF we need to wait for both the services and the vendors/purposes to be updated before continuing.
    Promise.all([denyAllServices(), denyAllForTCF(fromLayer)]).then(async () => {
      if (isAmpEnabled) {
        await UC.denyAllAmp();
      }
      setTrackingPixel(TRACKING_EVENT_TYPE.DENY_ALL, UC.setTrackingPixel);
      setCategories(UC.getCategories());
      setTcfData(UC.getTCFData());
    });
  };

  /**
   * A method to deny all consents
   * If UI_VARIANT not defined, method returns null
   */
  const denyAllConsents = (): Promise<void> | null => {
    switch (initialUIValues.variant) {
      case UI_VARIANT.CCPA:
        return acceptDenyCcpa(false);
      case UI_VARIANT.DEFAULT:
        return denyAllDefault();
      case UI_VARIANT.TCF:
        return denyAllTCF();
      default:
        return null;
    }
  };

  /**
   * A method to check if all consents were accepted
   * Will check purposes, services and vendors consents for TCF
   * And check only services for non-TCF
   */
  const areAllConsentsAccepted = (): boolean => {
    return UC.areAllConsentsAccepted();
  };

  const enableScriptsForServicesWithConsent = (): void => {
    return UC.enableScriptsForServicesWithConsent();
  };

  /**
   * This method should always be triggered when selecting a new language in the language selector dropdown.
   * @param selectedLanguage the language to be changed to
   */
  const onLanguageChange = async (selectedLanguage: string): Promise<void> => {
    const setting = UC.getSettings();

    if (RTL_LANGUAGES.includes(setting.ui.language.selected) !== RTL_LANGUAGES.includes(selectedLanguage)) {
      onThemeChange(generateTheme(setting.ui, RTL_LANGUAGES.includes(selectedLanguage), initialUIValues.variant));
    }

    if (!changeLanguageLoading) {
      await changeLanguage(selectedLanguage);
      // When we fetch a new language, we do not want "unsaved" user choices to be lost.
      // That's why we get the current category choices before we set it
      setCategories(getCurrentUiCategoriesChoices(UC.getCategories(), categories));
      setSettings(UC.getSettings());
      setLanguage(selectedLanguage);

      // TODO (EUD-344) keep yet unsaved toggle states for TCF, similar to the categories logic above.
      if (uiVariant === UI_VARIANT.TCF) {
        setTcfData(UC.getTCFData());
      }
    }
  };

  if (!window[UC_UI_GLOBAL_NAMESPACE]) {
    window[UC_UI_GLOBAL_NAMESPACE] = {
      acceptAllConsents,
      acceptService: (serviceId: string) => acceptDenyService(serviceId, true),
      areAllConsentsAccepted,
      clearStorage,
      closeCMP,
      denyAllConsents,
      denyAndCloseCcpa: async () => {
        await acceptDenyCcpa(false);
        closeCMP();
      },
      enableScriptsForServicesWithConsent,
      getActiveLanguage,
      getServices,
      getServicesBaseInfo,
      getServicesFullInfo,
      getSettings,
      getTCFVendors,
      isInitialized,
      rejectService: (serviceId: string) => acceptDenyService(serviceId, false),
      restartCMP,
      showFirstLayer,
      showSecondLayer,
      updateLanguage: (language: string) => onLanguageChange(language),
    };
  }

  if (view === VIEW.NONE) {
    return null;
  }

  return (
    <Suspense fallback={<Fragment />}>
      <View
        acceptAllDefault={acceptAllDefault}
        acceptAllTCF={acceptAllTCF}
        denyAllDefault={denyAllDefault}
        denyAllTCF={denyAllTCF}
        onClose={closeCMP}
        onLanguageChange={onLanguageChange}
        showSecondLayer={showSecondLayer}
        UC={UC}
      />
    </Suspense>
  );
};

export default App;
